import { Machine, assign, State, EventObject } from 'xstate'
import { debounceDelay } from './config'

type QueryInput = { query: string; [otherInputs: string]: string }

export interface DebouncedQueryMachineContext {
  getData: (input: any) => Promise<any>
  input?: QueryInput
  data?: any
  error?: Error
}

export interface DebouncedQueryMachineEvents extends EventObject {
  types: 'SEARCH_CHANGE' | 'SET_FUNCTION'
  input?: QueryInput
  func?: (input: any) => Promise<any>
}

export interface DebouncedQueryMachineStates {
  states: {
    idle: {}
    ready: {
      states: {
        idle: {}
        debouncing: {}
        searching: {}
        success: {}
        failed: {}
      }
    }
  }
}

const debouncedQueryMachineConfig = {
  actions: {
    setFunction: assign((__context: any, event: any) => ({
      getData: event.func,
    })),
    setData: assign((__context: any, event: any) => ({
      data: event.data,
    })),
    setInput: assign((__context: any, event: any) => ({
      input: event.input,
    })),
    resetData: assign((__context: any, event: any) => ({
      data: undefined,
    })),
    setError: assign((__context: any, event: any) => ({
      error: event.data,
    })),
  },
  guards: {
    isInputValid: (__context: any, event: any) => {
      return event.input.query.length > 1
    },
    isQueryEmpty: (__context: any, event: any) => {
      return (
        !event.input?.query ||
        event.input.query.length === 0 ||
        event.input.query.length < 2
      )
    },
  },
  services: {
    getTheData: async (__context: any) => {
      return await __context.getData(__context.input)
    },
  },
}
export const debouncedQueryMachine = Machine<
  DebouncedQueryMachineContext,
  DebouncedQueryMachineStates,
  DebouncedQueryMachineEvents
>(
  {
    id: 'search',
    initial: 'idle',
    context: {
      getData: () => Promise.resolve(undefined),
      input: undefined,
      data: undefined,
      error: undefined,
    },
    states: {
      idle: {
        on: {
          SET_FUNCTION: {
            target: 'ready.idle',
            actions: ['setFunction'],
          },
        },
      },
      ready: {
        initial: 'idle',
        on: {
          SEARCH_CHANGE: [
            {
              cond: 'isQueryEmpty',
              target: 'ready.idle',
              actions: ['resetData'],
            },
            {
              cond: 'isInputValid',
              target: 'ready.debouncing',
            },
          ],
          SET_FUNCTION: [
            {
              cond: 'isQueryEmpty',
              target: 'ready.idle',
              actions: ['setFunction', 'resetData'],
            },
            {
              cond: 'isInputValid',
              target: 'ready.debouncing',
            },
          ],
        },
        states: {
          idle: {},
          debouncing: {
            entry: 'setInput',
            after: {
              [debounceDelay]: 'searching',
            },
          },
          searching: {
            invoke: {
              id: 'getTheData',
              src: 'getTheData',
              onDone: {
                target: 'success',
                actions: ['setData'],
              },
              onError: {
                target: 'failed',
                actions: ['setError'],
              },
            },
          },
          success: {},
          failed: {},
        },
      },
    },
  },
  debouncedQueryMachineConfig
)

export type DebouncedQueryMachineState = State<
  DebouncedQueryMachineContext,
  DebouncedQueryMachineEvents,
  DebouncedQueryMachineStates
>
